/**
 * Prompt Templates React Query Hooks
 * Data fetching and mutations for prompt template management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { promptTemplatesApi } from '@/lib/api/promptTemplates';
import type {
  CreateTemplateRequest,
  UpdateTemplateRequest,
  PromptSection,
  SectionOrderItem,
} from '@/types/promptTemplates';

const QUERY_KEY = 'promptTemplates';

/**
 * Hook to fetch all templates
 */
export function usePromptTemplates(includeInactive = false) {
  return useQuery({
    queryKey: [QUERY_KEY, includeInactive],
    queryFn: () => promptTemplatesApi.list(includeInactive),
  });
}

/**
 * Hook to fetch a single template
 */
export function usePromptTemplate(templateId: number | null) {
  return useQuery({
    queryKey: [QUERY_KEY, templateId],
    queryFn: () => promptTemplatesApi.get(templateId!),
    enabled: templateId !== null,
  });
}

/**
 * Hook to fetch the default template
 */
export function useDefaultTemplate() {
  return useQuery({
    queryKey: [QUERY_KEY, 'default'],
    queryFn: () => promptTemplatesApi.getDefault(),
  });
}

/**
 * Hook to fetch available section types
 */
export function useAvailableSections() {
  return useQuery({
    queryKey: [QUERY_KEY, 'availableSections'],
    queryFn: () => promptTemplatesApi.getAvailableSections(),
  });
}

/**
 * Hook to create a new template
 */
export function useCreateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTemplateRequest) => promptTemplatesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

/**
 * Hook to update a template
 */
export function useUpdateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, data }: { templateId: number; data: UpdateTemplateRequest }) =>
      promptTemplatesApi.update(templateId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.templateId] });
    },
  });
}

/**
 * Hook to delete a template
 */
export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (templateId: number) => promptTemplatesApi.delete(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

/**
 * Hook to set a template as default
 */
export function useSetDefaultTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (templateId: number) => promptTemplatesApi.setDefault(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

/**
 * Hook to add a section to a template
 */
export function useAddSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, section }: { templateId: number; section: PromptSection }) =>
      promptTemplatesApi.addSection(templateId, section),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.templateId] });
    },
  });
}

/**
 * Hook to update a section in a template
 */
export function useUpdateSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      templateId,
      sectionId,
      section,
    }: {
      templateId: number;
      sectionId: string;
      section: PromptSection;
    }) => promptTemplatesApi.updateSection(templateId, sectionId, section),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.templateId] });
    },
  });
}

/**
 * Hook to delete a section from a template
 */
export function useDeleteSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, sectionId }: { templateId: number; sectionId: string }) =>
      promptTemplatesApi.deleteSection(templateId, sectionId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.templateId] });
    },
  });
}

/**
 * Hook to reorder sections in a template
 */
export function useReorderSections() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, orders }: { templateId: number; orders: SectionOrderItem[] }) =>
      promptTemplatesApi.reorderSections(templateId, orders),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.templateId] });
    },
  });
}
